import React, { useEffect, useState, useRef } from "react";
import baseURL from "../url";
import "./Banners.css";

export default function Banners() {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);

    const [index, setIndex] = useState(0);
    const intervalRef = useRef(null);
    const pauseRef = useRef(false);

    // Estado para volumen del video
    const [isMuted, setIsMuted] = useState(true);
    const [showVolumeBtn, setShowVolumeBtn] = useState(false);
    const volumeTimeoutRef = useRef(null);

    useEffect(() => {
        cargarBanners();
        return () => {
            if (volumeTimeoutRef.current) clearTimeout(volumeTimeoutRef.current);
        };
    }, []);

    const cargarBanners = () => {
        fetch(`${baseURL}/bannersGet.php`)
            .then((res) => res.json())
            .then((data) => {
                setImages(data.banner || []);
                setLoading(false);
            })
            .catch((error) => console.error("Error al cargar banners:", error));
    };

    // Autoplay inteligente
    useEffect(() => {
        if (images.length === 0) return;

        if (intervalRef.current) clearInterval(intervalRef.current);

        const currentBanner = images[index];
        // Si el banner actual es un video, pausamos el autoplay por tiempo
        if (currentBanner && currentBanner.tipo === "video") {
            return;
        }

        // Si es una imagen, transiciona cada 3 segundos
        intervalRef.current = setInterval(() => {
            if (!pauseRef.current) {
                setIndex((prev) => (prev + 1) % images.length);
            }
        }, 3000);

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [index, images, images.length]);

    // Mostrar el botón de volumen temporalmente al cambiar de banner si es un video
    useEffect(() => {
        const currentBanner = images[index];
        if (currentBanner && currentBanner.tipo === "video") {
            setIsMuted(true); // Altamente recomendado para asegurar la reproducción automática
            setShowVolumeBtn(true);
            if (volumeTimeoutRef.current) clearTimeout(volumeTimeoutRef.current);
            volumeTimeoutRef.current = setTimeout(() => {
                setShowVolumeBtn(false);
            }, 2000);
        }
    }, [index, images]);

    // Programar desaparición del botón a los 2 segundos
    const triggerVolumeBtn = () => {
        setShowVolumeBtn(true);
        if (volumeTimeoutRef.current) clearTimeout(volumeTimeoutRef.current);
        volumeTimeoutRef.current = setTimeout(() => {
            setShowVolumeBtn(false);
        }, 2000);
    };

    const toggleMute = (e) => {
        if (e) e.stopPropagation();
        setIsMuted((prev) => !prev);
        triggerVolumeBtn();
    };

    const handleContainerMouseEnter = () => {
        const currentBanner = images[index];
        if (currentBanner && currentBanner.tipo === "video") {
            setShowVolumeBtn(true);
            if (volumeTimeoutRef.current) clearTimeout(volumeTimeoutRef.current);
        }
    };

    const handleContainerMouseLeave = () => {
        const currentBanner = images[index];
        if (currentBanner && currentBanner.tipo === "video") {
            triggerVolumeBtn();
        }
    };

    const pauseAutoplay = () => {
        pauseRef.current = true;
        setTimeout(() => (pauseRef.current = false), 4000);
    };

    const next = () => {
        pauseAutoplay();
        setIndex((prev) => (prev + 1) % images.length);
    };

    const prev = () => {
        pauseAutoplay();
        setIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    const handleVideoEnded = () => {
        // Transiciona inmediatamente al siguiente banner cuando finaliza el video
        setIndex((prev) => (prev + 1) % images.length);
    };

    const handleClickBanner = () => {
        pauseAutoplay();
        const currentBanner = images[index];
        if (currentBanner && currentBanner.link) {
            window.open(currentBanner.link, "_blank");
        }
    };

    const handleVideoClick = (e) => {
        if (e) e.stopPropagation();
        const currentBanner = images[index];
        if (currentBanner && currentBanner.link) {
            handleClickBanner();
        } else {
            toggleMute(e);
        }
    };

    if (loading) return <div className="loadingBanner"></div>;
    if (images.length === 0) return null;

    const currentBanner = images[index];

    return (
        <div 
            style={styles.container}
            onMouseEnter={handleContainerMouseEnter}
            onMouseLeave={handleContainerMouseLeave}
        >
            {/* Imagen o Video */}
            <div 
                style={{ 
                    ...styles.imageWrapper, 
                    cursor: currentBanner && currentBanner.link ? "pointer" : "default" 
                }}
            >
                {currentBanner && currentBanner.tipo === "video" ? (
                    <video
                        src={currentBanner.imagen}
                        autoPlay
                        muted={isMuted}
                        playsInline
                        onEnded={handleVideoEnded}
                        onClick={handleVideoClick}
                        style={{ ...styles.image, objectFit: "cover" }}
                    />
                ) : (
                    <img
                        src={currentBanner ? currentBanner.imagen : ""}
                        alt={`banner-${index}`}
                        style={styles.image}
                        onClick={handleClickBanner}
                    />
                )}
            </div>

            {/* Botón de Audio/Silencio en el centro del Video */}
            {currentBanner && currentBanner.tipo === "video" && (
                <button
                    onClick={toggleMute}
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        background: 'rgba(0, 0, 0, 0.75)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '50%',
                        width: '60px',
                        height: '60px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 30,
                        opacity: showVolumeBtn ? 1 : 0,
                        pointerEvents: showVolumeBtn ? 'auto' : 'none',
                        transition: 'opacity 0.3s ease, transform 0.2s ease',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.4)',
                    }}
                    onMouseEnter={() => {
                        setShowVolumeBtn(true);
                        if (volumeTimeoutRef.current) clearTimeout(volumeTimeoutRef.current);
                    }}
                    onMouseLeave={() => {
                        triggerVolumeBtn();
                    }}
                >
                    {isMuted ? (
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                            <line x1="23" y1="9" x2="17" y2="15"></line>
                            <line x1="17" y1="9" x2="23" y2="15"></line>
                        </svg>
                    ) : (
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                            <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                        </svg>
                    )}
                </button>
            )}

            {/* Flecha izquierda */}
            <button style={{ ...styles.arrow, left: "15px" }} onClick={prev}>
                ‹
            </button>

            {/* Flecha derecha */}
            <button style={{ ...styles.arrow, right: "15px" }} onClick={next}>
                ›
            </button>

            {/* Indicadores */}
            <div style={styles.dots}>
                {images.map((_, i) => (
                    <div
                        key={i}
                        onClick={() => {
                            pauseAutoplay();
                            setIndex(i);
                        }}
                        style={{
                            ...styles.dot,
                            backgroundColor: index === i ? "#fff" : "rgba(255,255,255,0.5)",
                        }}
                    />
                ))}
            </div>
        </div>
    );
}

const styles = {
    container: {
        position: "relative",
        width: "80%",
        margin: "0 auto",
        overflow: "hidden",
        borderRadius: "20px",
    },
    imageWrapper: {
        width: "100%",
        height: "280px",
        borderRadius: "20px",
        overflow: "hidden",
    },
    image: {
        width: "100%",
        height: "100%",
        objectFit: "contain",
        objectPosition: "center",   
        display: "block",
        borderRadius: "20px",
        margin: "0 auto",          
    },
    arrow: {
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        width: "40px",
        height: "40px",
        background: "rgba(0,0,0,0.4)",
        borderRadius: "50%",
        fontSize: "24px",
        color: "#fff",
        border: "none",
        cursor: "pointer",
        zIndex: 20,
    },
    dots: {
        display: "flex",
        gap: "6px",
        position: "absolute",
        bottom: "12px",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 20,
    },
    dot: {
        width: "10px",
        height: "10px",
        borderRadius: "50%",
        cursor: "pointer",
        transition: "0.3s",
    },
};
